import { type BubbleRoleDto, type UserDto } from "@crowdcoursing/api/openapi";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import { type GridColDef } from "@mui/x-data-grid-pro";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";
import { useMemo, useRef, useState } from "react";

import { type ColumnData } from "~types/datagrid";
import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { DataGrid } from "~ui/molecules/DataGrid/DataGrid";

type ChartView = "orgGrowth" | "typeDistribution" | "typeGrowth";

const CHART_VIEW_STORAGE_KEY = "orgGrowthData.chartView";

const chartViewOptions: { label: string; value: ChartView }[] = [
  { label: "Member Growth Over Time", value: "orgGrowth" },
  { label: "Type Distribution", value: "typeDistribution" },
  { label: "Type Growth Over Time", value: "typeGrowth" },
];

const defaultTitle = "Organization Growth";
const defaultSubtitle = "Track organization membership growth over time";

export interface OrgGrowthDataProps {
  title?: string;
  subtitle?: string;
  data: UserDto[] | null;
  status: Utils.QueryStatus;
  onTimeRangeChange?: (newTimeRange: DateRangeValue) => void;
  timeRange?: DateRangeValue;
}

// Helper function to get date format based on date range
const getDateFormat = (dates: string[]) => {
  if (dates.length === 0) {
    return "MMM d";
  }
  const years = new Set(dates.map((d) => DateTime.fromFormat(d, "yyyy-MM-dd").year));
  return years.size > 1 ? "MM/yyyy" : "MMM d";
};

// Generate display dates with smart sampling based on range size
const generateDisplayDates = (startDate?: string, endDate?: string): string[] => {
  if (!startDate || !endDate) {
    return [];
  }

  const start = DateTime.fromFormat(startDate, "yyyy-MM-dd");
  const end = DateTime.fromFormat(endDate, "yyyy-MM-dd");
  const totalDays = Math.floor(end.diff(start, "days").days) + 1;

  let interval = 1;
  if (totalDays > 730) {
    interval = 30;
  } else if (totalDays > 180) {
    interval = 14;
  } else if (totalDays > 60) {
    interval = 7;
  }

  const result: string[] = [];
  let currentDate = start;

  while (currentDate <= end) {
    result.push(currentDate.toFormat("yyyy-MM-dd"));
    currentDate = currentDate.plus({ days: interval });
  }

  if (result[result.length - 1] !== endDate) {
    result.push(endDate);
  }

  return result;
};

// Extended role type that includes userId for deduplication
interface BubbleRoleWithUser extends BubbleRoleDto {
  userId: string;
}

// Extract all bubble roles from users, preserving userId for deduplication
const extractAllBubbleRoles = (users: UserDto[]): BubbleRoleWithUser[] => {
  const roles: BubbleRoleWithUser[] = [];
  users.forEach((user) => {
    if (user.bubbleRoles && user.id) {
      user.bubbleRoles.forEach((role) => {
        roles.push({ ...role, userId: user.id ?? "" });
      });
    }
  });
  return roles;
};

// Aggregate by bubble type for pie chart - counts unique users per type
const aggregateByBubbleType = (roles: BubbleRoleWithUser[]): { name: string; count: number }[] => {
  const usersByType: Record<string, Set<string>> = {};
  roles.forEach((r) => {
    const type = r.bubbleType || "Unknown";
    // Filter out Site Admin
    if (type === "Site Admin") {
      return;
    }
    if (!usersByType[type]) {
      usersByType[type] = new Set();
    }
    usersByType[type].add(r.userId);
  });
  return Object.entries(usersByType)
    .map(([name, userSet]) => ({ count: userSet.size, name }))
    .sort((a, b) => b.count - a.count);
};

// Aggregate bubble type growth over time - counts unique users per type
const aggregateTypeGrowthByDay = (
  roles: BubbleRoleWithUser[],
  startDate?: string,
  endDate?: string,
): { dates: string[]; series: { name: string; data: number[] }[] } => {
  // Filter out Site Admin roles
  const filteredRoles = roles.filter((r) => (r.bubbleType || "Unknown") !== "Site Admin");

  const allTypes = new Set<string>();
  filteredRoles.forEach((r) => allTypes.add(r.bubbleType || "Unknown"));

  // Track unique users by date and type
  const usersByDateAndType: Record<string, Record<string, Set<string>>> = {};
  filteredRoles.forEach((r) => {
    if (r.timestamp) {
      const date = DateTime.fromISO(r.timestamp).toFormat("yyyy-MM-dd");
      if (!usersByDateAndType[date]) {
        usersByDateAndType[date] = {};
      }
      const type = r.bubbleType || "Unknown";
      if (!usersByDateAndType[date][type]) {
        usersByDateAndType[date][type] = new Set();
      }
      usersByDateAndType[date][type].add(r.userId);
    }
  });

  const sortedDates = Object.keys(usersByDateAndType).sort();

  // Calculate baseline unique users before start date
  const baselineUsers: Record<string, Set<string>> = {};
  allTypes.forEach((type) => (baselineUsers[type] = new Set()));

  if (startDate) {
    sortedDates
      .filter((d) => d < startDate)
      .forEach((d) => {
        Object.entries(usersByDateAndType[d]).forEach(([type, users]) => {
          users.forEach((userId) => baselineUsers[type].add(userId));
        });
      });
  }

  const displayDates = generateDisplayDates(startDate, endDate);
  if (displayDates.length === 0) {
    return { dates: [], series: [] };
  }

  const series: { name: string; data: number[] }[] = Array.from(allTypes).map((type) => ({
    data: [],
    name: type,
  }));

  displayDates.forEach((displayDate) => {
    series.forEach((s) => {
      // Collect all unique users up to this display date
      const cumulativeUsers = new Set(baselineUsers[s.name]);
      sortedDates
        .filter((d) => d >= (startDate || "") && d <= displayDate)
        .forEach((d) => {
          usersByDateAndType[d]?.[s.name]?.forEach((userId) => cumulativeUsers.add(userId));
        });
      s.data.push(cumulativeUsers.size);
    });
  });

  return { dates: displayDates, series };
};

// Aggregate organization membership growth over time - counts unique users per org
const aggregateOrgGrowthByDay = (
  roles: BubbleRoleWithUser[],
  startDate?: string,
  endDate?: string,
): { dates: string[]; series: { name: string; data: number[] }[] } => {
  // Filter out Site Admin roles
  const filteredRoles = roles.filter((r) => (r.bubbleType || "Unknown") !== "Site Admin");

  const allOrgs = new Set<string>();
  filteredRoles.forEach((r) => allOrgs.add(r.bubbleName || "Unknown"));

  // Track unique users by date and organization
  const usersByDateAndOrg: Record<string, Record<string, Set<string>>> = {};
  filteredRoles.forEach((r) => {
    if (r.timestamp) {
      const date = DateTime.fromISO(r.timestamp).toFormat("yyyy-MM-dd");
      if (!usersByDateAndOrg[date]) {
        usersByDateAndOrg[date] = {};
      }
      const org = r.bubbleName || "Unknown";
      if (!usersByDateAndOrg[date][org]) {
        usersByDateAndOrg[date][org] = new Set();
      }
      usersByDateAndOrg[date][org].add(r.userId);
    }
  });

  const sortedDates = Object.keys(usersByDateAndOrg).sort();

  // Calculate baseline unique users before start date
  const baselineUsers: Record<string, Set<string>> = {};
  allOrgs.forEach((org) => (baselineUsers[org] = new Set()));

  if (startDate) {
    sortedDates
      .filter((d) => d < startDate)
      .forEach((d) => {
        Object.entries(usersByDateAndOrg[d]).forEach(([org, users]) => {
          users.forEach((userId) => baselineUsers[org].add(userId));
        });
      });
  }

  const displayDates = generateDisplayDates(startDate, endDate);
  if (displayDates.length === 0) {
    return { dates: [], series: [] };
  }

  const series: { name: string; data: number[] }[] = Array.from(allOrgs).map((org) => ({
    data: [],
    name: org,
  }));

  displayDates.forEach((displayDate) => {
    series.forEach((s) => {
      // Collect all unique users up to this display date
      const cumulativeUsers = new Set(baselineUsers[s.name]);
      sortedDates
        .filter((d) => d >= (startDate || "") && d <= displayDate)
        .forEach((d) => {
          usersByDateAndOrg[d]?.[s.name]?.forEach((userId) => cumulativeUsers.add(userId));
        });
      s.data.push(cumulativeUsers.size);
    });
  });

  return { dates: displayDates, series };
};

// Type Distribution Pie Chart
interface TypeDistributionRow {
  rowId: string;
  name: string;
  count: number;
  percentage: number;
}

const TypeDistributionChart = ({ data, loading }: { data: UserDto[]; loading: boolean }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const allRoles = useMemo(() => extractAllBubbleRoles(data), [data]);
  const typeData = useMemo(() => aggregateByBubbleType(allRoles), [allRoles]);

  // Calculate total for percentage
  const total = useMemo(() => typeData.reduce((sum, item) => sum + item.count, 0), [typeData]);

  // Create rows for DataGrid
  const rows: TypeDistributionRow[] = useMemo(
    () =>
      typeData.map((item) => ({
        count: item.count,
        name: item.name,
        percentage: total > 0 ? Math.round((item.count / total) * 1000) / 10 : 0,
        rowId: item.name,
      })),
    [typeData, total],
  );

  const columns = useMemo<GridColDef<TypeDistributionRow>[]>(
    () => [
      {
        field: "name",
        flex: 2,
        headerName: "Name",
        minWidth: 150,
      },
      {
        align: "right",
        field: "count",
        flex: 1,
        headerAlign: "right",
        headerName: "Count",
        minWidth: 80,
      },
      {
        align: "right",
        field: "percentage",
        flex: 1,
        headerAlign: "right",
        headerName: "%",
        minWidth: 70,
        valueFormatter: ({ value }: { value: number }) => `${value}%`,
      },
    ],
    [],
  );

  const pieData = useMemo(
    () =>
      typeData.map((item) => ({
        name: String(item.name),
        y: Number(item.count),
      })),
    [typeData],
  );

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "pie",
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          },
        },
      },
      series: [
        {
          data: [...pieData],
          name: "Members",
          type: "pie",
        },
      ],
      title: {
        text: "Type Distribution",
      },
      tooltip: {
        pointFormat: "{series.name}: <b>{point.y}</b> ({point.percentage:.1f}%)",
      },
    }),
    [pieData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (typeData.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">No org type data available</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {/* Pie Chart - Left Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
      </Grid>

      {/* DataGrid Table - Right Half */}
      <Grid size={{ xl: 6, xs: 12 }}>
        <Box sx={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}>
          <DataGrid<TypeDistributionRow>
            columns={columns as ColumnData<TypeDistributionRow>[]}
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            paginationMode="client"
            rowHeight={40}
            rows={rows}
            slotProps={{
              container: {
                sx: { height: "auto", maxHeight: 400, minHeight: "unset" },
              },
              empty: {
                message: "No data available",
              },
            }}
            slots={{
              toolbar: () => null,
            }}
            sortingMode="client"
            status={loading ? "pending" : "success"}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

// Type Growth Over Time Chart
const TypeGrowthChart = ({
  data,
  loading,
  timeRange,
}: {
  data: UserDto[];
  loading: boolean;
  timeRange?: DateRangeValue;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startDateStr = timeRange?.startDate?.toFormat("yyyy-MM-dd");
  const endDateStr = timeRange?.endDate?.toFormat("yyyy-MM-dd");

  const allRoles = useMemo(() => extractAllBubbleRoles(data), [data]);
  const growthData = useMemo(
    () => aggregateTypeGrowthByDay(allRoles, startDateStr, endDateStr),
    [allRoles, endDateStr, startDateStr],
  );

  const dateFormat = useMemo(() => getDateFormat(growthData.dates), [growthData.dates]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: 350,
        type: "line",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            enabled: growthData.dates.length <= 30,
            radius: 3,
          },
        },
      },
      series: growthData.series.map((s) => ({
        data: [...s.data],
        name: s.name,
        type: "line" as const,
      })),
      title: {
        text: "Type Growth Over Time",
      },
      tooltip: {
        formatter: function () {
          const points = this.points?.filter((p) => (p.y ?? 0) > 0).sort((a, b) => (b.y ?? 0) - (a.y ?? 0)) ?? [];
          if (points.length === 0) {
            return false;
          }
          let html = `<b>${this.x}</b><br/>`;
          points.forEach((p) => {
            html += `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${p.y}</b><br/>`;
          });
          return html;
        },
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories: growthData.dates.map((d) => DateTime.fromFormat(d, "yyyy-MM-dd").toFormat(dateFormat)),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Total Members",
        },
      },
    }),
    [dateFormat, growthData],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (growthData.dates.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
        <Typography color="text.secondary">No org type data available for this period</Typography>
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />;
};

// Org Growth Over Time Chart
const ORG_FILTER_STORAGE_KEY = "orgGrowthData.orgGrowth.selectedOrgs";
const ORG_TYPE_FILTER_STORAGE_KEY = "orgGrowthData.orgGrowth.selectedTypes";

const OrgGrowthChart = ({
  data,
  loading,
  timeRange,
}: {
  data: UserDto[];
  loading: boolean;
  timeRange?: DateRangeValue;
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const startDateStr = timeRange?.startDate?.toFormat("yyyy-MM-dd");
  const endDateStr = timeRange?.endDate?.toFormat("yyyy-MM-dd");

  const allRoles = useMemo(() => extractAllBubbleRoles(data), [data]);

  // Get all unique org types for the type filter dropdown (excluding Site Admin)
  const allOrgTypes = useMemo(() => {
    const types = new Set<string>();
    allRoles.forEach((r) => {
      const type = r.bubbleType || "Unknown";
      if (type !== "Site Admin") {
        types.add(type);
      }
    });
    return Array.from(types).sort();
  }, [allRoles]);

  // Type filter state
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(ORG_TYPE_FILTER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Filter roles by selected types before aggregation
  const filteredRolesByType = useMemo(() => {
    if (selectedTypes.length === 0) {
      return allRoles;
    }
    return allRoles.filter((r) => selectedTypes.includes(r.bubbleType || "Unknown"));
  }, [allRoles, selectedTypes]);

  const growthData = useMemo(
    () => aggregateOrgGrowthByDay(filteredRolesByType, startDateStr, endDateStr),
    [filteredRolesByType, endDateStr, startDateStr],
  );

  const allOrgNames = useMemo(() => {
    return growthData.series.map((s) => s.name).sort();
  }, [growthData.series]);

  const [selectedOrgs, setSelectedOrgs] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(ORG_FILTER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const filteredSeries = useMemo(() => {
    const validOrgs = selectedOrgs.filter((org) => allOrgNames.includes(org));
    if (validOrgs.length === 0) {
      return growthData.series;
    }
    return growthData.series.filter((s) => validOrgs.includes(s.name));
  }, [growthData.series, selectedOrgs, allOrgNames]);

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newTypes = typeof value === "string" ? value.split(",") : value;
    setSelectedTypes(newTypes);
    localStorage.setItem(ORG_TYPE_FILTER_STORAGE_KEY, JSON.stringify(newTypes));
  };

  const handleOrgChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const newOrgs = typeof value === "string" ? value.split(",") : value;
    setSelectedOrgs(newOrgs);
    localStorage.setItem(ORG_FILTER_STORAGE_KEY, JSON.stringify(newOrgs));
  };

  const handleClearFilters = () => {
    setSelectedTypes([]);
    setSelectedOrgs([]);
    localStorage.removeItem(ORG_TYPE_FILTER_STORAGE_KEY);
    localStorage.removeItem(ORG_FILTER_STORAGE_KEY);
  };

  const hasFilters = selectedTypes.length > 0 || selectedOrgs.length > 0;

  const dateFormat = useMemo(() => getDateFormat(growthData.dates), [growthData.dates]);

  const legendRows = Math.ceil(allOrgNames.length / 3);
  const chartHeight = Math.max(450, 350 + legendRows * 22);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },
      chart: {
        height: chartHeight,
        type: "line",
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
        maxHeight: 200,
        navigation: {
          activeColor: "#3b82f6",
          enabled: true,
          inactiveColor: "#cccccc",
        },
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          marker: {
            enabled: growthData.dates.length <= 30,
            radius: 3,
          },
        },
      },
      series: filteredSeries.map((s) => ({
        data: [...s.data],
        name: s.name,
        type: "line" as const,
      })),
      title: {
        text: "Member Growth Over Time",
      },
      tooltip: {
        formatter: function () {
          const points = this.points?.filter((p) => (p.y ?? 0) > 0).sort((a, b) => (b.y ?? 0) - (a.y ?? 0)) ?? [];
          if (points.length === 0) {
            return false;
          }
          let html = `<b>${this.x}</b><br/>`;
          points.forEach((p) => {
            html += `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${p.y}</b><br/>`;
          });
          return html;
        },
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories: growthData.dates.map((d) => DateTime.fromFormat(d, "yyyy-MM-dd").toFormat(dateFormat)),
        title: {
          text: "Date",
        },
      },
      yAxis: {
        allowDecimals: false,
        min: 0,
        title: {
          text: "Total Members",
        },
      },
    }),
    [chartHeight, dateFormat, filteredSeries, growthData.dates],
  );

  if (loading) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 450, justifyContent: "center" }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  if (growthData.dates.length === 0) {
    return (
      <Box sx={{ alignItems: "center", display: "flex", height: 450, justifyContent: "center" }}>
        <Typography color="text.secondary">No organization data available for this period</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="org-type-filter-label">Filter by Type</InputLabel>
          <Select
            id="org-type-filter-select"
            input={<OutlinedInput label="Filter by Type" />}
            label="Filter by Type"
            labelId="org-type-filter-label"
            multiple
            onChange={handleTypeChange}
            renderValue={(selected) =>
              selected.length === 0
                ? "All Types"
                : selected.length === 1
                  ? selected[0]
                  : `${selected.length} types selected`
            }
            value={selectedTypes}
          >
            {allOrgTypes.map((typeName) => (
              <MenuItem key={typeName} value={typeName}>
                <Checkbox checked={selectedTypes.includes(typeName)} size="small" />
                <ListItemText primary={typeName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel id="org-filter-label">Filter Organizations</InputLabel>
          <Select
            id="org-filter-select"
            input={<OutlinedInput label="Filter Organizations" />}
            label="Filter Organizations"
            labelId="org-filter-label"
            multiple
            onChange={handleOrgChange}
            renderValue={(selected) =>
              selected.length === 0
                ? "All Organizations"
                : selected.length === 1
                  ? selected[0]
                  : `${selected.length} organizations selected`
            }
            value={selectedOrgs}
          >
            {allOrgNames.map((orgName) => (
              <MenuItem key={orgName} value={orgName}>
                <Checkbox checked={selectedOrgs.includes(orgName)} size="small" />
                <ListItemText primary={orgName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasFilters && (
          <Box sx={{ alignItems: "center", display: "flex" }}>
            <Chip label="Clear filters" onDelete={handleClearFilters} size="small" variant="outlined" />
          </Box>
        )}
      </Box>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
    </Box>
  );
};

const getInitialChartView = (): ChartView => {
  const stored = localStorage.getItem(CHART_VIEW_STORAGE_KEY);
  if (stored === "orgGrowth" || stored === "typeDistribution" || stored === "typeGrowth") {
    return stored;
  }
  return "orgGrowth";
};

export const OrgGrowthData = ({
  title = defaultTitle,
  subtitle = defaultSubtitle,
  data,
  status,
  onTimeRangeChange,
  timeRange,
}: OrgGrowthDataProps) => {
  const [chartView, setChartView] = useState<ChartView>(getInitialChartView);
  const loading = status === "pending";
  const error = status === "error";

  const defaultTimeRange: DateRangeValue = {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ days: 30 }),
  };

  const handleTimeRangeChange = (newRange: DateRangeValue) => {
    onTimeRangeChange?.(newRange);
  };

  const handleChartViewChange = (event: SelectChangeEvent) => {
    const newView = event.target.value as ChartView;
    setChartView(newView);
    localStorage.setItem(CHART_VIEW_STORAGE_KEY, newView);
  };

  const renderChart = () => {
    switch (chartView) {
      case "orgGrowth":
        return <OrgGrowthChart data={data ?? []} loading={loading} timeRange={timeRange} />;
      case "typeDistribution":
        return <TypeDistributionChart data={data ?? []} loading={loading} />;
      case "typeGrowth":
        return <TypeGrowthChart data={data ?? []} loading={loading} timeRange={timeRange} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 2, pt: 1 }}>
      {/* How to Read This Data */}
      <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, mb: 3, p: 2 }}>
        <Typography sx={{ mb: 1 }} variant="h6">
          How to Read This Data
        </Typography>
        <Typography color="text.secondary" variant="body2">
          <strong>Member Growth Over Time:</strong> Shows how membership in each organization has grown over time. You
          can filter by organization type or specific organizations.
          <br />
          <strong>Type Distribution:</strong> Shows the breakdown of organization types (Schools, Districts, Companies,
          etc.) as a pie chart.
          <br />
          <strong>Type Growth Over Time:</strong> Tracks how each organization type category has grown over the selected
          period.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <Typography sx={{ mb: 0.5 }} variant="h6">
                {title}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {subtitle}
              </Typography>
            </Box>
            <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="org-growth-chart-view-label">View</InputLabel>
                <Select
                  id="org-growth-chart-view-select"
                  label="View"
                  labelId="org-growth-chart-view-label"
                  onChange={handleChartViewChange}
                  value={chartView}
                >
                  {chartViewOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ width: 240 }}>
                <PopoverDateRangePicker
                  onChange={handleTimeRangeChange}
                  size="small"
                  value={timeRange ?? defaultTimeRange}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid size={12}>
          {error ? (
            <Box sx={{ alignItems: "center", display: "flex", height: 350, justifyContent: "center" }}>
              <Typography color="error">Failed to load organization data</Typography>
            </Box>
          ) : (
            renderChart()
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
